import { FileInfo } from '../../../types'
import { EventEmitter } from '../utils/EventEmitter'
import { OperationStatus, RunnableOperation } from './RunnableOperation'

export class TransactionBlock {
  private _operations: RunnableOperation[]
  public requiresCommit: boolean
  private eventEmitter: EventEmitter
  private aborted: boolean = false
  private completed: boolean = false
  private running: boolean = false
  public id: string
  public progress: number = 0
  public status: OperationStatus = 'pending'
  public type: string = ''

  constructor(operations: RunnableOperation[], requiresCommit: boolean = true) {
    this._operations = operations
    this.requiresCommit = requiresCommit
    this.eventEmitter = new EventEmitter()
    this.id = this.generateId()
    this.setupOperationListeners()
    this.type = operations[0].type
  }

  private setupOperationListeners() {
    this._operations.forEach((operation) => {
      operation.onStatusUpdate((status) => {
        this.updateBlockStatus(status)
      })

      operation.onProgressUpdate((progress) => {
        this.updateBlockProgress(progress)
      })
    })
  }

  private updateBlockStatus(status: OperationStatus) {
    this.status = status
    if (this._operations.every((op) => op.status === 'completed')) {
      this.complete()
    } else if (this._operations.some((op) => op.status === 'error')) {
      this.abort()
    }
    this.notify('statusUpdated', this.status)
  }

  private updateBlockProgress(progress: number) {
    const totalProgress = this._operations.reduce(
      (sum, op) => sum + op.getProgress(),
      0
    )
    this.progress = Math.round(totalProgress / this._operations.length)
    this.notify('progressUpdated', this.progress)
  }

  get operations(): RunnableOperation[] {
    return this._operations
  }

  get operationLabel(): string {
    return this?._operations[0]?.type || 'LABEL'
  }

  get operationFile(): FileInfo | undefined {
    return this?._operations[0]?.userOpSpec?.file as FileInfo | undefined
  }

  addEventListener(
    eventName: string,
    listener: (...args: any[]) => void
  ): void {
    this.eventEmitter.on(eventName, listener)
  }

  removeEventListener(
    eventName: string,
    listener: (...args: any[]) => void
  ): void {
    this.eventEmitter.off(eventName, listener)
  }

  notify(eventName: string, data?: any): void {
    this.eventEmitter.emit(eventName, data)
  }

  abort(): void {
    if (!this.aborted) {
      this.aborted = true
      this.status = 'aborted'
      this._operations.forEach((operation) => {
        if (typeof operation.abort === 'function') {
          operation.abort()
        }
      })
      this.notify('aborted')
    }
  }

  isAborted(): boolean {
    return this.aborted
  }

  complete(): void {
    this.completed = true
    this.running = false
    this.notify('completed')
  }

  isCompleted(): boolean {
    return this.completed
  }

  isRunning(): boolean {
    return this.running
  }

  start(): void {
    this.running = true
    this.notify('started')
  }

  private generateId(): string {
    return Math.random().toString(36).substr(2, 9)
  }

  on(eventName: string, listener: (...args: any[]) => void): void {
    this.eventEmitter.on(eventName, listener)
  }

  off(eventName: string, listener: (...args: any[]) => void): void {
    this.eventEmitter.off(eventName, listener)
  }
}
